import React, { useMemo } from 'react';
import { Box, Button, Stack } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import styles from './PageErrorBlock.styles';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ReactComponent as DenyIcon } from './icon/deny-ico.svg';
import { ReactComponent as NotFoundIcon } from './icon/404-ico.svg';
import { ReactComponent as ServerErrorIcon } from './icon/500-ico.svg';

interface PageErrorBlockProps {
    errorType?: string;
    showBackHomeBtn?: boolean;
    additionalButtonsSlot?: React.JSX.Element;
    iconImageUrl?: string;
    errorTitle?: string;
    errorMessage?: string;
    errorCssClass?: string;
    errorList?: string[];
}

export default function PageErrorBlock(props: PageErrorBlockProps) {
    const { t } = useBundleTranslation(['components/error/page_error_block']);
    const {
        errorType,
        showBackHomeBtn = true,
        additionalButtonsSlot = null,
        iconImageUrl,
        errorTitle,
        errorMessage,
        errorCssClass,
        errorList,
    } = props;

    const supportAdmins = useSelector((state: any) => state.auth?.userInfo?.supportAdmins) ?? [];

    const IconComponent = useMemo(() => {
        if (iconImageUrl) return <Box component="img" src={iconImageUrl} />;

        if (errorType === 'not_found' || errorType === 'notfound') return <NotFoundIcon />;

        if (errorType === 'internal_server') return <ServerErrorIcon />;

        return <DenyIcon />;
    }, [errorType, iconImageUrl]);

    const ErrorMessageComponent = useMemo(() => {
        if (errorType) {
            return (
                <>
                    {errorTitle && <Box sx={styles.title}>{errorTitle}</Box>}
                    {errorMessage && (
                        <Box sx={styles.message}>
                            <Box className={errorCssClass ?? undefined}>
                                <Box
                                    component={'span'}
                                    dangerouslySetInnerHTML={{ __html: errorMessage }}
                                    sx={{ 'p:last-of-type': { mb: 0 }, 'p:first-of-type': { mt: 0 } }}
                                />
                            </Box>
                        </Box>
                    )}
                </>
            );
        }

        if (!errorList) return null;
        return (
            <>
                {errorList.map((item, index) => {
                    //todo @htmlentities // htmlspecialchars
                    return <Box key={index}>{item}</Box>;
                })}
            </>
        );
    }, [errorType, errorTitle, errorMessage, errorCssClass, errorList]);

    return (
        <Box component="main" sx={styles.container}>
            <Stack direction="row" justifyContent="space-between" sx={styles.dataWrapper}>
                <Stack spacing={1} sx={styles.messageContainer}>
                    {ErrorMessageComponent}

                    {supportAdmins.length > 0 && (
                        <Box sx={styles.supportAdminsContainer}>
                            <Box sx={{ pb: 2 }}>{t('access_support_administrators')}</Box>
                            <Stack spacing={1} sx={{ color: 'primary.main' }}>
                                {supportAdmins.map((user: any) => {
                                    return (
                                        <Stack direction="row" key={user.username}>
                                            <Box sx={styles.supportAdminsIconHolder}>
                                                <IconMi icon={'user-profile'} />
                                            </Box>
                                            <Box>{user.display_name}</Box>
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        </Box>
                    )}

                    <Stack sx={styles.buttonsContainer} direction={'row'} spacing={0.5} alignItems={'center'}>
                        {showBackHomeBtn && (
                            <Button data-test={'return-button'} component={Link} to={'/home'} sx={{ flexShrink: 0 }}>
                                {t('return_btn_label')}
                            </Button>
                        )}
                        {additionalButtonsSlot}
                    </Stack>
                </Stack>
                <Box sx={styles.iconHolder}>{IconComponent}</Box>
            </Stack>
        </Box>
    );
}
